import React, { useState, useEffect } from 'react';
import TeamLogo from '../../components/TeamLogo';
import { Result } from '../../models/Result';
import GameFilter from '../../components/GameFilter';
import { ResultsContainer, LoadingState, ErrorState, PageTitle, ResultCard } from './Results.Styles';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { entriesUrl, CACHE_KEYS } from '../../utils/apiEndpoints';

const Results: React.FC = () => {
  const [results, setResults] = useState<Result[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter] = useState<'all' | 'home' | 'away'>('all');

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const items = await fetchItemsWithCache<Result>(
          entriesUrl('nomads_results'),
          CACHE_KEYS.results,
          5 * 60 * 1000
        );

        setResults(items);
        setLoading(false);
      } catch (err) {
        setError('Unable to load results. Please try again later.');
        setLoading(false);
      }
    };

    fetchResults();
  }, []);

  const filteredResults = results
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .filter(result => {
      if (filter === 'home') return result.homeTeam === 'Pollington Nomads';
      if (filter === 'away') return result.awayTeam === 'Pollington Nomads';
      return true;
    });

  if (loading) {
    return (
      <ResultsContainer>
        <LoadingState>Loading results...</LoadingState>
      </ResultsContainer>
    );
  }

  if (error) {
    return (
      <ResultsContainer>
        <ErrorState>{error}</ErrorState>
      </ResultsContainer>
    );
  }

  return (
    <ResultsContainer>
      <PageTitle>Match Results</PageTitle>
      
      <GameFilter />
      
      {filteredResults.map((result, index) => (
        <ResultCard key={`${result.date}-${result.homeTeam}-${result.awayTeam}`}>
          <div className="match-header">
            <div className="team">
              <div className="logo-container">
                <TeamLogo team={result.homeTeam} width="60px" height="60px" />
              </div>
              <h3>{result.homeTeam}</h3>
            </div>
            
            <div className="score">
              {result.homeScore} - {result.awayScore}
            </div>
            
            <div className="team">
              <div className="logo-container">
                <TeamLogo team={result.awayTeam} width="60px" height="60px" />
              </div>
              <h3>{result.awayTeam}</h3>
            </div>
          </div>
          
          <div className="match-details">
            <div className="detail">
              <h4>Date</h4>
              <p>{new Date(result.date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}</p>
            </div>
            
            <div className="detail">
              <h4>Venue</h4>
              <p>{result.venue}</p>
            </div>
            
            <div className="detail">
              <h4>Man of the Match</h4>
              <p>{result.motm}</p>
            </div>
            
            <div className="detail">
              <h4>Phucknose</h4>
              <p>{result.nomadOfTheGame}</p>
            </div>
          </div>
        </ResultCard>
      ))}
      
      <p style={{ textAlign: 'center', color: 'var(--gray)', marginTop: '2rem' }}>
        * Results are updated after each game
      </p>
    </ResultsContainer>
  );
};

export default Results; 